import { AddressCreateWithoutCustomerInput } from '@/__generated__/graphql';
import { useMutation } from '@apollo/client';
import { Button, Group, Select, Stack, TextInput } from '@mantine/core';
import { isNotEmpty, matches, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  setPaymentMethodOnCheckoutOrderDocument,
  setShippingMethodOnCheckoutOrderDocument,
  upsertAddressOnCheckoutOrderDocument,
} from '../graphql/mutations';
import { checkoutOrderDocument } from '../graphql/queries';

export const AddressForm = ({
  orderId,
  address,
  isPersonal,
}: {
  orderId: string;
  address?: AddressCreateWithoutCustomerInput;
  isPersonal: boolean;
}) => {
  const [setShippingMethod] = useMutation(setShippingMethodOnCheckoutOrderDocument, {
    refetchQueries: [checkoutOrderDocument],
  });

  const [setPaymentMethod] = useMutation(setPaymentMethodOnCheckoutOrderDocument, {
    refetchQueries: [checkoutOrderDocument],
  });

  const [upsertAddressOnCheckoutOrder] = useMutation(upsertAddressOnCheckoutOrderDocument, {
    refetchQueries: [checkoutOrderDocument],
    onCompleted: async () => {
      modals.closeAll();

      if (isPersonal) {
        await setShippingMethod({
          variables: {
            orderId,
            shippingMethodId: 'cm4bow5ql0005n82snqsojj6y',
            isPersonal: true,
          },
        });
        await setPaymentMethod({
          variables: {
            orderId,
            paymentMethodId: 'cm4bow5qp0009n82su9fyiisa',
            isPersonal: true,
          },
        });
      }
    },
  });

  const form = useForm<AddressCreateWithoutCustomerInput>({
    initialValues: {
      id: address?.id,
      name: address?.name ?? '',
      streetLine: address?.streetLine ?? '',
      streetDescription: address?.streetDescription,
      city: address?.city ?? '',
      postCode: address?.postCode ?? '',
      country: address?.country ?? 'SK',
      phone: address?.phone ?? '',
    },
    validate: {
      name: !isPersonal ? isNotEmpty('Meno a priezvisko sú povinné') : undefined,
      streetLine: !isPersonal ? isNotEmpty('Ulica je povinná') : undefined,
      city: !isPersonal ? isNotEmpty('Mesto je povinné') : undefined,
      postCode: !isPersonal
        ? (value, formValues) =>
            isNotEmpty('PSČ je povinné')(value) ||
            matches(
              {
                SK: /^\d{3}\s?\d{2}$/,
                CZ: /^\d{3}\s?\d{2}$/,
                HU: /^\d{4}$/,
                AT: /^\d{4}$/,
                DE: /^\d{5}$/,
                BE: /^\d{4}$/,
              }[formValues.country] || /.*/,
              'Neplatné PSČ pre vybranú krajinu'
            )(value)
        : undefined,
      country: !isPersonal ? isNotEmpty('Štát je povinný') : undefined,
      phone: (value) =>
        isPersonal
          ? isNotEmpty('Telefónne číslo je povinné pre osobný odber')(value) ||
            matches(
              /^(\+421|0)[1-9][0-9]{8}$/,
              'Telefónne číslo musí byť platné'
            )(value.replace(/\s+/g, ''))
          : isNotEmpty('Telefónne číslo je povinné pre kuriéra')(value) ||
            matches(
              /^(?:\+421\s?\d{3}\s?\d{3}\s?\d{3}|\+420\s?\d{3}\s?\d{3}\s?\d{3}|\+36\s?\d{1,2}\s?\d{3}\s?\d{4}|\+43\s?\d{1,4}\s?\d{3,10}|\+49\s?\d{1,4}\s?\d{3,10}|\+32\s?\d{1,4}\s?\d{3,10}|\+33\s?\d{1,2}\s?\d{2,3}\s?\d{2,4})$/,
              'Telefónne číslo musí začínať s predvoľbou (napr. +421 900 123 456)'
            )(value),
    },
  });

  return (
    <form
      onSubmit={form.onSubmit(
        (address) =>
          void upsertAddressOnCheckoutOrder({
            variables: {
              orderId,
              address,
            },
          })
      )}
    >
      <Stack>
        {!isPersonal && (
          <>
            <TextInput
              label="Meno a priezvisko"
              placeholder="napr. Janko Hraško"
              autoComplete="name"
              required
              {...form.getInputProps('name')}
            />
            <TextInput
              label="Ulica a číslo domu"
              placeholder="napr. Hlavná 123"
              autoComplete="address-line1"
              required
              {...form.getInputProps('streetLine')}
            />
            <TextInput
              label="Popis adresy (voliteľné)"
              placeholder="napr. 3. poschodie, č. dverí 12"
              autoComplete="address-line2"
              {...form.getInputProps('streetDescription')}
            />
            <Group grow>
              <TextInput
                label="Mesto"
                placeholder="napr. Košice"
                autoComplete="address-level2"
                required
                {...form.getInputProps('city')}
              />
              <TextInput
                label="PSČ"
                placeholder="napr. 04001"
                autoComplete="postal-code"
                required
                {...form.getInputProps('postCode')}
              />
            </Group>
            <Select
              data={[
                { value: 'SK', label: 'Slovensko' },
                { value: 'CZ', label: 'Česko' },
                { value: 'HU', label: 'Maďarsko' },
                { value: 'AT', label: 'Rakúsko' },
                { value: 'DE', label: 'Nemecko' },
                { value: 'BE', label: 'Belgicko' },
                { value: 'FR', label: 'Francúzsko' },
              ]}
              label="Krajina"
              placeholder="Zvoľte krajinu"
              allowDeselect={false}
              autoComplete="country"
              required
              {...form.getInputProps('country')}
            />
          </>
        )}
        <TextInput
          type="tel"
          label="Telefónne číslo"
          placeholder={isPersonal ? 'napr. 0940 123 456' : 'napr. +421 900 123 456'}
          autoComplete="tel"
          required
          {...form.getInputProps('phone')}
        />
        <Button type="submit" flex={1}>
          Uložiť {isPersonal ? 'telefónne číslo' : 'adresu'}
        </Button>
      </Stack>
    </form>
  );
};
